<!--
  Component-level runtime health.

  V1 showed one "broadcast server reachable" light. That is not enough for a platform
  that owns its own runtime, so each component reports its own state, and each state
  renders as icon + text + colour.
-->
<script setup lang="ts">
import { ICONS } from '../icons.js';

export interface HealthEntry {
  component: string;
  label: string;
  state: 'UP' | 'DEGRADED' | 'DOWN' | 'UNKNOWN';
  detail?: string;
  latencyMs?: number;
}

defineProps<{ entries: HealthEntry[] }>();

const STATE_ICONS = {
  UP: ICONS.CircleCheck,
  DEGRADED: ICONS.CircleAlert,
  DOWN: ICONS.CircleX,
  UNKNOWN: ICONS.CircleHelp,
} as const;

const TONE = {
  UP: 'success',
  DEGRADED: 'warning',
  DOWN: 'danger',
  UNKNOWN: 'neutral',
} as const;
</script>

<template>
  <ul class="shio-health">
    <li v-for="entry in entries" :key="entry.component" class="shio-health__item">
      <component
        :is="STATE_ICONS[entry.state]"
        :size="16"
        class="shio-health__icon"
        :data-state="entry.state"
        aria-hidden="true"
      />
      <div class="shio-health__text">
        <span class="shio-body-sm">{{ entry.label }}</span>
        <span v-if="entry.detail" class="shio-meta">{{ entry.detail }}</span>
      </div>
      <span class="shio-badge" :class="`shio-badge--${TONE[entry.state]}`">{{ entry.state }}</span>
      <span v-if="entry.latencyMs !== undefined" class="shio-meta shio-tabular"
        >{{ entry.latencyMs }} ms</span
      >
    </li>
  </ul>
</template>

<style scoped>
.shio-health {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.shio-health__item {
  display: flex;
  align-items: center;
  gap: var(--shio-space-3);
  height: var(--shio-row-height);
  border-bottom: var(--shio-border-width) solid hsl(var(--shio-border) / 0.4);
}

.shio-health__item:last-child {
  border-bottom: 0;
}

.shio-health__text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.shio-health__icon[data-state='UP'] {
  color: hsl(var(--shio-status-success));
}
.shio-health__icon[data-state='DEGRADED'] {
  color: hsl(var(--shio-status-warning));
}
.shio-health__icon[data-state='DOWN'] {
  color: hsl(var(--shio-status-danger));
}
.shio-health__icon[data-state='UNKNOWN'] {
  color: hsl(var(--shio-text-muted));
}
</style>
